import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { History, Transition } from 'history';
import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router';
import type { Navigator } from 'react-router-dom';
import { useStableCallback } from '../hooks/useStableCallback';

// As of August 2025, this still seems to be the only way to block route transitions
// without major refactoring.
// React-router-dom 6.30 supports useBlocker. I tried it, but it can be used only with some "data router",
// which - as I've found - we don't use. Revisit this eventually with router v7 or so.
type ExtendNavigator = Navigator & Pick<History, 'block'>;
let routeTransitionBlockersMounted = 0;

type Props = {
  blockTransitions: boolean;
  onBlock: () => Promise<boolean>;
};

/**
 * Block route transitions and handle them with a custom callback for leave-dialog-like features
 *
 * Features:
 * - warning on using multiple transition blockers at once, as that would lead into quirky results
 * (multiple leave dialogs at once?).
 * - toggleable by `blockTransitions` prop, usually just directly using `dirty` state of a form.
 * - providing simple Promise api, compared to legacy variant using a callback API
 *   overriding retry() to enforce the transition.
 * - tip: use with useAsyncModal
 *
 * @example
 * useRouteTransitionBlocker({
 *   blockTransitions: form.dirty,
 *   onBlock: () => window.confirm('Form is not saved, really leave?');
 * });
 *
 * // supports async onBlock
 * useRouteTransitionBlocker({
 *   blockTransitions: form.dirty,
 *   onBlock: async () => {
 *     if (await myFancyConfirm('Form is not saved, really leave?')) {
 *       console.log('Leaving');
 *       return true;
 *     }
 *     return false;
 *   }
 * });
 */
export const useRouteTransitionBlocker = (props: Props): void => {
  const navigatorContext = useContext(NavigationContext);

  // Passing unstable onBlock causes the blocker to be reinitialized at every render
  // and effectively blocking the transition every time. To keep robust usage from outside,
  // stabilize the identity there, always calling the most up-to-date one but passing
  // stable identity to the effect.
  const onBlock = useStableCallback(props.onBlock);

  useEffect(() => {
    if (!(props.blockTransitions ?? true)) {
      return undefined;
    }

    if (routeTransitionBlockersMounted > 0) {
      stumble(
        'Multiple route transition blockers mounted at once will probably result into glitches',
      );
    }
    routeTransitionBlockersMounted += 1;

    const unblock = (navigatorContext.navigator as ExtendNavigator).block(
      (transition) => {
        onBlock().then((result) => {
          if (result) {
            unblock();
            transition.retry();
          }
        });
      },
    );

    return () => {
      routeTransitionBlockersMounted -= 1;
      unblock();
    };
  }, [navigatorContext.navigator, props.blockTransitions]);
};

/**
 * Legacy usage:
 *  - auto-unblocking - the API was designed in a slightly confusing way.
 *  - fiddling with locationState used by some legacy components
 *  - non-object props argument, difficult to extend
 *  - not handled stability of onBlock (see implementation of the new useRouteTransitionBlocker)
 *
 * Has to be in the same file as the new implementation to simply share
 * the counter and warn about multiple overlapping usages.
 */
export const useRouteTransitionBlockerLegacy = (
  onBlock: (transition: Transition) => void,
  blockTransitions = true,
): void => {
  const navigatorContext = useContext(NavigationContext);

  useEffect(() => {
    if (!blockTransitions) {
      return undefined;
    }

    if (routeTransitionBlockersMounted > 0) {
      stumble(
        'Multiple route transition blockers mounted at once will probably result into glitches',
      );
    }
    routeTransitionBlockersMounted += 1;

    const unblock = (navigatorContext.navigator as ExtendNavigator).block(
      (transition) => {
        const autoUnblockingTransition = {
          ...transition,
          retry() {
            unblock();
            const locationState = transition.location.state as {
              canLeave?: boolean;
            };
            // If canLeave was forced it have to be reset because the browser preserves state and the user goes back,
            // he can face unexpected behaviour - he will be able to leave the page without confirmation even if
            // he didn't save changes
            if (locationState && locationState.canLeave !== undefined) {
              locationState.canLeave = undefined;
            }
            transition.retry();
          },
        };

        onBlock(autoUnblockingTransition);
      },
    );

    return () => {
      routeTransitionBlockersMounted -= 1;
      unblock();
    };
  }, [navigatorContext.navigator, onBlock, blockTransitions]);
};
